import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetEvents,
  adminModerateEvent,
  adminGetBanners,
  adminModerateBanner,
  adminGetReports,
  adminDismissReports,
  adminGrantListingCredits,
  EVENT_REMOVE_REASONS,
  BANNER_REMOVE_REASONS,
} from "../lib/bannerApi";

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n, w) => (n === 1 ? w : w + "s");
const EV_PAGE_SIZE = 50;

// Remove flow: a quick reason dropdown + one confirm button (no text boxes).
const RemoveBox = ({ reasons, onConfirm, onCancel }) => {
  const [reason, setReason] = useState("");
  return (
    <div className="mt-2 rounded-xl border border-[#ff2a00]/30 bg-[#ff2a00]/5 p-3">
      <select
        data-testid="remove-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full rounded-lg border border-white/10 bg-[#121212] px-3 py-2 text-sm text-white"
      >
        <option value="">Select a reason…</option>
        {reasons.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <div className="mt-2 flex gap-2">
        <button
          data-testid="confirm-remove"
          disabled={!reason}
          onClick={() => onConfirm(reason)}
          className="flex-1 rounded-full bg-[#ff2a00] py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          Confirm remove
        </button>
        <button onClick={onCancel} className="rounded-full border border-white/15 px-3 py-2 text-xs text-white/60">
          Cancel
        </button>
      </div>
    </div>
  );
};

const ActionBtns = ({ children }) => (
  <div className="mt-2 flex flex-wrap gap-2">{children}</div>
);
const Btn = ({ onClick, children, testid, tone = "" }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${tone || "border-white/15 text-white/80"}`}
  >
    {children}
  </button>
);

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [banners, setBanners] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [removing, setRemoving] = useState(null); // id being removed
  const [eventFilter, setEventFilter] = useState("active");
  const [bannerFilter, setBannerFilter] = useState("active");
  const [eventQ, setEventQ] = useState("");
  const [evTotal, setEvTotal] = useState(0);
  const [evCounts, setEvCounts] = useState(null);
  const [evPage, setEvPage] = useState(1);
  const [evLoading, setEvLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantCredits, setGrantCredits] = useState("");
  const [granting, setGranting] = useState(false);

  const doGrant = async () => {
    const email = grantEmail.trim();
    const n = parseInt(grantCredits, 10);
    if (!email) return toast.error("Enter a user email");
    if (!n || n < 1) return toast.error("Enter a valid number of credits");
    setGranting(true);
    try {
      const ref = `admin_grant:${(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`)}`;
      const res = await adminGrantListingCredits(email, n, ref);
      toast.success(`Added ${res.credits} listing ${res.credits === 1 ? "credit" : "credits"} to ${res.email}`);
      setGrantEmail("");
      setGrantCredits("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add credits.");
    } finally {
      setGranting(false);
    }
  };

  // Events are filtered, searched and paged on the server over the WHOLE catalogue
  // (support #279162); the page used to receive only the newest 100 events.
  const evReq = useRef(0);
  const loadEvents = () => {
    const id = ++evReq.current;
    setEvLoading(true);
    return adminGetEvents({ status: eventFilter, q: eventQ.trim(), page: evPage, page_size: EV_PAGE_SIZE })
      .then((d) => {
        if (id !== evReq.current) return;
        setEvents(d.items);
        setEvTotal(d.total);
        setEvCounts(d.counts);
      })
      .catch((err) => {
        if (id !== evReq.current) return;
        if (err?.response?.status === 403) setDenied(true);
        else toast.error("Could not load events.");
      })
      .finally(() => {
        if (id === evReq.current) setEvLoading(false);
      });
  };

  const load = () => {
    setLoading(true);
    adminGetBanners()
      .then(setBanners)
      .catch((err) => {
        if (err?.response?.status === 403) setDenied(true);
        else toast.error("Could not load admin data.");
      })
      .finally(() => setLoading(false));
    adminGetReports().then(setReports).catch(() => setReports([]));
    loadEvents();
  };

  const evFirstRun = useRef(true);
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (evFirstRun.current) {
      evFirstRun.current = false;
      return undefined;
    }
    const t = setTimeout(loadEvents, 300);
    return () => clearTimeout(t);
  }, [eventFilter, eventQ, evPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const doEvent = async (id, action, reason) => {
    try {
      await adminModerateEvent(id, action, reason);
      toast.success({ hide: "Event hidden", remove: "Event removed", restore: "Event restored" }[action] || "Done");
      setRemoving(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };
  const doBanner = async (id, action, reason) => {
    try {
      await adminModerateBanner(id, action, reason);      toast.success(`Banner ${action}d`);
      setRemoving(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  if (denied) {
    return (
      <div data-testid="admin-denied" className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#050505] px-6 text-center text-white">
        <p className="font-display text-xl font-bold">Admins only</p>
        <button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#ff2a00] px-5 py-2.5 text-sm font-bold">
          Back to RoadRnr
        </button>
      </div>
    );
  }

  const doReport = async (eventId, action) => {
    try {
      if (action === "hide") await adminModerateEvent(eventId, "hide");
      if (action === "remove") await adminModerateEvent(eventId, "remove", "Reported by users");
      await adminDismissReports(eventId);
      toast.success(action === "dismiss" ? "Report dismissed" : action === "hide" ? "Event hidden" : "Event removed");
      load();
    } catch {
      toast.error("Action failed.");
    }
  };

  const EV_FILTERS = ["active", "past", "hidden", "removed"];
  const BN_FILTERS = ["active", "hidden", "expired", "removed"];
  const EV_BADGE = {
    active: "border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e]",
    past: "border-white/20 bg-white/5 text-white/50",
    hidden: "border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000]",
    removed: "border-[#ff2a00]/40 bg-[#ff2a00]/10 text-[#ff2a00]",
  };
  const BN_BADGE = { active: EV_BADGE.active, hidden: EV_BADGE.hidden, removed: EV_BADGE.removed, expired: "border-white/20 bg-white/5 text-white/50" };
  const evCat = (e) => {
    const st = (e.status || "").toLowerCase();
    if (st === "hidden") return "hidden";
    if (st === "removed") return "removed";
    const end = e.end_at || e.start_at;
    return end && new Date(end).getTime() < Date.now() ? "past" : "active";
  };
  const evShown = events; // already filtered, searched and paged by the server
  const bnShown = banners.filter((b) => (b.display_status || "").toLowerCase() === bannerFilter);
  const openReportCount = reports.reduce((s, r) => s + (r.count || 0), 0);
  const flagged = new Set(reports.map((r) => r.event_id));

  return (
    <div data-testid="admin-page" className="min-h-[100dvh] bg-[#050505] pb-20 text-white">
      <div className="rr-on-image sticky top-0 z-30 border-b border-white/10 bg-[#050505]/90 px-5 pb-3 pt-6">
        <button onClick={() => navigate("/")} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-white/60">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-white">Admin</h1>
        <button
          data-testid="admin-open-agent"
          onClick={() => navigate("/admin/agent")}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-4 py-1.5 text-sm font-semibold text-[#ff2a00] transition-colors hover:bg-[#ff2a00]/20"
        >
          <Bot className="h-4 w-4" /> AI Admin Agent
        </button>
        <div className="mt-3 flex gap-2">
          {["events", "reports", "banners"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t ? "bg-[#ff2a00] text-white" : "border border-white/15 text-white/70"}`}
            >
              {t === "events" ? "Events" : t === "reports" ? "Reports" : "Banner Ads"}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-3 px-5 pt-4">
        <div data-testid="admin-manual-credits" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
          <h2 className="text-sm font-bold text-white">Manual Listing Credits</h2>
          <p className="mt-0.5 text-xs text-white/50">
            Grant paid listing credits to a user by email. Each behaves like a purchased credit.
          </p>
          <input
            data-testid="grant-email"
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="User email"
            className="mt-3 w-full rounded-lg border border-white/10 bg-[#121212] px-3 py-2 text-base text-white placeholder:text-white/40 sm:text-sm"
          />
          <input
            data-testid="grant-credits"
            type="number"
            min="1"
            value={grantCredits}
            onChange={(e) => setGrantCredits(e.target.value)}
            placeholder="Number of credits"
            className="mt-2 w-full rounded-lg border border-white/10 bg-[#121212] px-3 py-2 text-base text-white placeholder:text-white/40 sm:text-sm"
          />
          <button
            data-testid="grant-submit"
            onClick={doGrant}
            disabled={granting}
            className="mt-3 w-full rounded-full bg-[#ff2a00] py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            {granting ? "Adding…" : "Add credits"}
          </button>
        </div>

        {tab === "events" ? (
          <>
            <div className="flex gap-2">
              {EV_FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`event-filter-${f}`}
                  onClick={() => {
                    setEventFilter(f);
                    setEvPage(1);
                  }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize ${eventFilter === f ? EV_BADGE[f] : "border-white/15 text-white/50"}`}
                >
                  {f}{evCounts ? ` (${evCounts[f]})` : ""}
                </button>
              ))}
            </div>
            <input
              data-testid="admin-event-search"
              value={eventQ}
              onChange={(e) => {
                setEventQ(e.target.value);
                setEvPage(1);
              }}
              placeholder="Search all events by name, venue or address…"
              className="w-full rounded-xl border border-white/10 bg-[#121212] px-3 py-2 text-base sm:text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
          </>
        ) : tab === "banners" ? (
          <div className="flex flex-wrap gap-2">
            {BN_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`banner-filter-${f}`}
                onClick={() => setBannerFilter(f)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize ${bannerFilter === f ? BN_BADGE[f] : "border-white/15 text-white/50"}`}
              >
                {f}
              </button>
            ))}
          </div>
        ) : null}
        {!loading && (
          <p data-testid="admin-count" className="text-xs text-white/40">
            {tab === "events"
              ? `${evTotal} ${cap(eventFilter)} ${plural(evTotal, "event")}${evTotal > EV_PAGE_SIZE ? ` · page ${evPage} of ${Math.ceil(evTotal / EV_PAGE_SIZE)}` : ""}`
              : tab === "reports"
              ? `${openReportCount} ${plural(openReportCount, "open report")}`
              : `${bnShown.length} ${cap(bannerFilter)} ${plural(bnShown.length, "banner ad")}`}
          </p>
        )}
        {(tab === "events" ? evLoading : loading) ? (
          <div className="flex justify-center pt-10"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>
        ) : tab === "events" ? (
          <>
          {evShown.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold">{e.name}</p>
                <div className="flex shrink-0 items-center gap-1">
                  {flagged.has(e.id) && (
                    <span data-testid={`event-flagged-${e.id}`} className="rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#ff2a00]">Flagged</span>
                  )}
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${EV_BADGE[evCat(e)] || "border-white/15 text-white/60"}`}>{evCat(e)}</span>
                </div>
              </div>
              <p className="truncate text-xs text-white/40">{e.location}</p>
              {removing === `e-${e.id}` ? (
                <RemoveBox reasons={EVENT_REMOVE_REASONS} onConfirm={(r) => doEvent(e.id, "remove", r)} onCancel={() => setRemoving(null)} />
              ) : (
                <ActionBtns>
                  <Btn testid={`event-hide-${e.id}`} onClick={() => doEvent(e.id, "hide")}>Hide</Btn>
                  <Btn testid={`event-restore-${e.id}`} onClick={() => doEvent(e.id, "restore")}>Restore</Btn>
                  <Btn testid={`event-remove-${e.id}`} tone="border-[#ff2a00]/40 text-[#ff2a00]" onClick={() => setRemoving(`e-${e.id}`)}>Remove</Btn>
                </ActionBtns>
              )}
            </div>
          ))}
          {!evShown.length && (
            <p data-testid="admin-event-empty" className="py-6 text-center text-sm text-white/40">No events match.</p>
          )}
          {evTotal > EV_PAGE_SIZE && (
            <div data-testid="admin-event-pager" className="flex items-center justify-between pt-2 text-xs text-white/60">
              <Btn testid="admin-event-prev" onClick={() => setEvPage((p) => Math.max(1, p - 1))}>Previous</Btn>
              <span>Page {evPage} of {Math.ceil(evTotal / EV_PAGE_SIZE)}</span>
              <Btn testid="admin-event-next" onClick={() => setEvPage((p) => Math.min(Math.ceil(evTotal / EV_PAGE_SIZE), p + 1))}>Next</Btn>
            </div>
          )}
          </>
        ) : tab === "banners" ? (
          bnShown.map((b) => (
            <div key={b.id} data-testid={`admin-banner-${b.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-3">
              {b.display_url && <img src={b.display_url} alt="" className="aspect-[3/1] w-full rounded-xl bg-[#0e0e0e] object-contain" />}
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${BN_BADGE[(b.display_status || "").toLowerCase()] || "border-white/15 text-white/60"}`}>{b.display_status}</span>
                <span className="text-xs text-white/40">£{((b.amount_pence || 0) / 100 || 9.99).toFixed(2)}</span>
              </div>
              <p className="mt-1 truncate text-xs text-white/50">{b.destination_url}</p>
              <p className="truncate text-[10px] text-white/30">ends {b.ends_at || "—"}</p>
              {removing === `b-${b.id}` ? (
                <RemoveBox reasons={BANNER_REMOVE_REASONS} onConfirm={(r) => doBanner(b.id, "remove", r)} onCancel={() => setRemoving(null)} />
              ) : (
                <ActionBtns>
                  <Btn testid={`banner-hide-${b.id}`} onClick={() => doBanner(b.id, "hide")}>Hide</Btn>
                  <Btn testid={`banner-restore-${b.id}`} onClick={() => doBanner(b.id, "restore")}>Restore</Btn>
                  <Btn testid={`banner-remove-${b.id}`} tone="border-[#ff2a00]/40 text-[#ff2a00]" onClick={() => setRemoving(`b-${b.id}`)}>Remove</Btn>
                </ActionBtns>
              )}
            </div>
          ))
        ) : (
          reports.map((r) => (
            <div key={r.event_id} data-testid={`admin-report-${r.event_id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold">{r.event_name}</p>
                <span className="shrink-0 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#ff2a00]">
                  {r.count} {plural(r.count, "report")}
                </span>
              </div>
              <p className="mt-1 text-[10px] text-white/30">reported {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</p>
              <ActionBtns>
                <Btn testid={`report-view-${r.event_id}`} onClick={() => navigate(`/event/${r.event_id}`)}>View Event</Btn>
                <Btn testid={`report-dismiss-${r.event_id}`} onClick={() => doReport(r.event_id, "dismiss")}>Dismiss</Btn>
                <Btn testid={`report-hide-${r.event_id}`} onClick={() => doReport(r.event_id, "hide")}>Hide</Btn>
                <Btn testid={`report-remove-${r.event_id}`} tone="border-[#ff2a00]/40 text-[#ff2a00]" onClick={() => doReport(r.event_id, "remove")}>Remove</Btn>
              </ActionBtns>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
